import { useState, useRef } from 'react';
import { useCaptureOverlay } from '../hooks/useCaptureOverlay';

import { makeImageErrorHandler, resetRecoverStage } from './PhotoUpload';

export interface UploadedDocument {
  id: string;
  name: string;
  type: 'document'; // Always 'document' for this component
  dataUrl: string; // Base64 data URL for preview
  file: File;
  timestamp: Date;
}

interface DocumentUploadProps {
  uploads: UploadedDocument[];
  onUpload: (file: UploadedDocument) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  maxUploads?: number;
  required?: boolean;
  title?: string;
  description?: string;
  // See PhotoUpload — durable thumbnail recovery for queued image documents
  // after iOS evicts the in-memory blob backing (#2127, diag #54).
  recoverBlobUrl?: (uploadId: string) => Promise<string | null>;
}

function generateId(): string {
  return `doc-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function DocumentUpload({
  uploads,
  onUpload,
  onRemove,
  disabled = false,
  maxUploads = 5,
  required = false,
  title = 'Documents',
  description = 'Upload set forms, paperwork, or completion documents',
  recoverBlobUrl,
}: DocumentUploadProps) {
  const [isCapturing, setIsCapturing] = useState(false);
  // Cancelling the camera fires no change event, so this is what un-sticks
  // the overlay when the rep backs out to pick an existing photo (#3448).
  useCaptureOverlay(isCapturing, () => setIsCapturing(false));
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<UploadedDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (uploads.length >= maxUploads) {
        alert(`Maximum ${maxUploads} documents allowed`);
        break;
      }

      const dataUrl = await readFileAsDataUrl(file);

      // Always type as 'document' - user chose this section explicitly
      const uploadedFile: UploadedDocument = {
        id: generateId(),
        name: file.name,
        type: 'document',
        dataUrl,
        file,
        timestamp: new Date(),
      };

      onUpload(uploadedFile);
    }

    // Reset the input
    event.target.value = '';
    setIsCapturing(false);
  };

  const readFileAsDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCameraCapture = () => {
    setIsCapturing(true);
    cameraInputRef.current?.click();
  };

  const handleGallerySelect = () => {
    fileInputRef.current?.click();
  };

  const handlePreview = (file: UploadedDocument) => {
    setPreviewFile(file);
    setPreviewUrl(file.dataUrl);
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewFile(null);
  };

  const canAddMore = uploads.length < maxUploads && !disabled;
  const isImage = (file: UploadedDocument) => file.file.type.startsWith('image/');

  return (
    <div className="rounded-xl bg-white p-4 shadow-card">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-heading text-sm font-medium uppercase tracking-wide text-slate-500">
            {title}
          </h3>
          {required && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
              Required
            </span>
          )}
        </div>
        <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
          {uploads.length}/{maxUploads}
        </span>
      </div>

      {/* Description */}
      <p className="mb-3 text-xs text-slate-400">{description}</p>

      {/* Upload Buttons */}
      {canAddMore && (
        <div className="mb-4 flex gap-2">
          <button
            onClick={handleCameraCapture}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 py-3 text-sm font-medium text-amber-700 transition-colors hover:border-amber-400 hover:bg-amber-100"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>Photo of Form</span>
          </button>
          <button
            onClick={handleGallerySelect}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-amber-300 bg-amber-50/50 py-3 text-sm font-medium text-amber-700 transition-colors hover:border-amber-400 hover:bg-amber-100"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Upload File</span>
          </button>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Uploaded Files Grid */}
      {uploads.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {uploads.map((upload) => (
            <div
              key={upload.id}
              className="group relative aspect-square overflow-hidden rounded-lg bg-amber-50 border border-amber-200"
            >
              {isImage(upload) ? (
                <img
                  src={upload.dataUrl}
                  alt={upload.name}
                  className="h-full w-full object-cover cursor-pointer"
                  onClick={() => handlePreview(upload)}
                  onLoad={resetRecoverStage}
                  onError={makeImageErrorHandler(upload.file, upload.id, recoverBlobUrl)}
                />
              ) : (
                <div
                  className="flex h-full w-full cursor-pointer flex-col items-center justify-center p-2"
                  onClick={() => handlePreview(upload)}
                >
                  <svg className="h-8 w-8 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="mt-1 text-[10px] text-amber-600 truncate w-full text-center">
                    {upload.name.length > 12 ? upload.name.slice(0, 12) + '...' : upload.name}
                  </span>
                </div>
              )}

              {/* Remove button */}
              {!disabled && (
                <button
                  onClick={() => onRemove(upload.id)}
                  className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {/* Type indicator */}
              <div className="absolute bottom-1 left-1 rounded bg-amber-600 px-1.5 py-0.5 text-[10px] text-white">
                DOC
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {uploads.length === 0 && !canAddMore && (
        <div className="py-6 text-center text-sm text-slate-400">
          No uploads allowed
        </div>
      )}

      {uploads.length === 0 && canAddMore && (
        <div className="py-4 text-center text-sm text-slate-400">
          {required
            ? 'Please upload set completion forms or paperwork'
            : 'No documents uploaded yet'
          }
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && previewFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={closePreview}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]">
            {isImage(previewFile) ? (
              <img
                src={previewUrl}
                alt={previewFile.name}
                className="max-h-[80vh] rounded-lg object-contain"
                onLoad={resetRecoverStage}
                onError={makeImageErrorHandler(previewFile.file, previewFile.id, recoverBlobUrl)}
              />
            ) : (
              <div className="rounded-lg bg-white p-8 text-center">
                <svg className="mx-auto h-16 w-16 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-4 font-medium text-slate-700">{previewFile.name}</p>
                <p className="mt-1 text-sm text-slate-500">
                  {(previewFile.file.size / 1024).toFixed(1)} KB
                </p>
              </div>
            )}
            <button
              onClick={closePreview}
              className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-lg"
            >
              <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="mt-4 text-center text-sm text-white">
              {previewFile.timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
          </div>
        </div>
      )}

      {/* Capturing indicator */}
      {isCapturing && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50">
          <div className="rounded-lg bg-white p-4 text-center">
            <div className="h-8 w-8 mx-auto animate-spin rounded-full border-4 border-amber-600 border-t-transparent"></div>
            <p className="mt-2 text-sm text-slate-600">Opening camera...</p>
          </div>
        </div>
      )}
    </div>
  );
}
